import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

export type RegionalOperationsView="SUBMISSIONS"|"DISTRIBUTION"|"READINESS";

type Submission={
  submissionId:string;schoolId:string;schoolName:string;schoolCode?:string|null;yearCode:string;
  templateName:string;templateVersionLabel:string;revision:number;status:string;submitNote?:string|null;
  submittedAt:string;reviewedAt?:string|null;decisionNote?:string|null;
};
type Inbox={counts:{submitted:number;returned:number;approved:number};items:Submission[]};
type School={schoolId:string;name:string;code?:string|null;studentCount:number};
type Assessment={versionId:string;instrumentName:string;title:string;versionLabel:string;gradeMin?:number|null;gradeMax?:number|null};
type Distribution={distributionId:string;versionId:string;assessmentTitle:string;status:string;schoolCount:number;requestedLearnerCount:number;succeededCount:number;skippedCount:number;failedCount:number;createdAt:string};
type DistributionWorkspace={schools:School[];assessments:Assessment[];distributions:Distribution[]};
type Readiness={ready:boolean;checks:Array<{key:string;label:string;pass:boolean}>;externalNotificationQueue:{queued:number;accepted:number;retryableFailure:number;permanentFailure:number}};

function fmt(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
}
function statusLabel(value:string){
  const map:Record<string,string>={SUBMITTED:"검토 대기",RETURNED:"보완 요청",APPROVED:"승인",SUCCEEDED:"완료",PARTIAL:"부분 완료",FAILED:"실패"};
  return map[value] || value;
}

export function AdminRegionalOperations({
  client,rootOrganizationId,view
}:{
  client:PlatformClient;
  rootOrganizationId:string;
  view:RegionalOperationsView;
}){
  const [inbox,setInbox]=useState<Inbox|null>(null);
  const [distribution,setDistribution]=useState<DistributionWorkspace|null>(null);
  const [readiness,setReadiness]=useState<Readiness|null>(null);
  const [selectedSchools,setSelectedSchools]=useState<string[]>([]);
  const [versionId,setVersionId]=useState("");
  const [notes,setNotes]=useState<Record<string,string>>({});
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    setError("");
    Promise.all([
      client.rpc("admin_report_submission_inbox",{p_root_organization_id:rootOrganizationId}),
      client.rpc("admin_multi_school_assessment_workspace",{p_root_organization_id:rootOrganizationId}),
      client.rpc("admin_production_readiness",{p_root_organization_id:rootOrganizationId})
    ]).then(([a,b,c])=>{
      if(!active)return;
      const first=a.error || b.error || c.error;
      if(first){setError(first.message);return;}
      const nextDistribution=b.data as DistributionWorkspace;
      setInbox(a.data as Inbox);
      setDistribution(nextDistribution);
      setReadiness(c.data as Readiness);
      setVersionId((current)=>nextDistribution.assessments.some((item)=>item.versionId===current) ? current : nextDistribution.assessments[0]?.versionId || "");
      setSelectedSchools((current)=>current.filter((id)=>nextDistribution.schools.some((school)=>school.schoolId===id)));
    });
    return()=>{active=false;};
  },[client,rootOrganizationId,refresh]);

  const allSelected=Boolean(distribution?.schools.length) && selectedSchools.length===distribution?.schools.length;
  const selectedAssessment=useMemo(()=>distribution?.assessments.find((item)=>item.versionId===versionId) || null,[distribution,versionId]);

  async function decide(item:Submission,decision:"APPROVE"|"RETURN"){
    setBusy(item.submissionId+decision);setError("");setMessage("");
    const {error}=await client.rpc("admin_decide_school_report_submission",{
      p_root_organization_id:rootOrganizationId,
      p_submission_id:item.submissionId,
      p_decision:decision,
      p_note:notes[item.submissionId]?.trim() || null
    });
    setBusy("");
    if(error){setError(error.message);return;}
    setMessage(`${item.schoolName} 보고서를 ${decision==="APPROVE" ? "승인" : "보완 요청"} 처리했습니다.`);
    setRefresh((value)=>value+1);
  }

  async function distribute(){
    if(!selectedSchools.length || !versionId)return;
    setBusy("distribution");setError("");setMessage("");
    const {data,error}=await client.rpc("admin_distribute_assessment_to_schools",{
      p_root_organization_id:rootOrganizationId,
      p_version_id:versionId,
      p_school_ids:selectedSchools
    });
    setBusy("");
    if(error){setError(error.message);return;}
    const result=data as {status:string;schoolCount:number;succeededCount:number;failedCount:number};
    setMessage(`${result.schoolCount}개 학교 배포 작업: ${statusLabel(result.status)} · 성공 ${result.succeededCount} · 실패 ${result.failedCount}`);
    setSelectedSchools([]);
    setRefresh((value)=>value+1);
  }

  async function retryNotifications(){
    setBusy("retry");setError("");setMessage("");
    const {data,error}=await client.rpc("admin_retry_external_notifications",{p_root_organization_id:rootOrganizationId});
    setBusy("");
    if(error){setError(error.message);return;}
    const result=data as {eligibleCount:number;triggered:boolean};
    setMessage(result.triggered ? `재시도 가능한 외부 알림 ${result.eligibleCount}건의 Worker를 호출했습니다.` : "현재 재시도 가능한 외부 알림이 없습니다.");
    setRefresh((value)=>value+1);
  }

  if(error)return <p className="error">{error}</p>;

  if(view==="SUBMISSIONS"){
    return (
      <section className="tower-card erp08-admin-panel">
        <div className="panel-title"><div><strong>학교 공식 보고서 제출함</strong><span>학교가 제출한 스냅샷을 접수·반려·승인합니다.</span></div></div>
        {message && <p className="teacher-success">{message}</p>}
        <div className="erp08-kpis">
          <div><span>검토 대기</span><strong>{inbox?.counts.submitted ?? 0}</strong></div>
          <div><span>보완 요청</span><strong>{inbox?.counts.returned ?? 0}</strong></div>
          <div><span>승인</span><strong>{inbox?.counts.approved ?? 0}</strong></div>
        </div>
        <div className="erp08-inbox-list">
          {(inbox?.items || []).map((item)=>(
            <article key={item.submissionId} className={item.status==="SUBMITTED" ? "erp08-inbox-row active" : "erp08-inbox-row"}>
              <header>
                <div><strong>{item.schoolName}</strong><span>{item.yearCode} · {item.templateName} {item.templateVersionLabel} · {item.revision}차</span></div>
                <span className={item.status==="APPROVED" ? "status-pill good" : "status-pill"}>{statusLabel(item.status)}</span>
              </header>
              <div className="erp08-inbox-meta"><span>제출 {fmt(item.submittedAt)}</span>{item.submitNote && <span>학교 메모: {item.submitNote}</span>}</div>
              {item.decisionNote && <p className="erp08-review-note">검토 메모: {item.decisionNote}</p>}
              {item.status==="SUBMITTED" && (
                <div className="erp08-review-actions">
                  <textarea rows={2} value={notes[item.submissionId] || ""} onChange={(event)=>setNotes((current)=>({...current,[item.submissionId]:event.target.value}))} placeholder="승인/보완 요청 메모" />
                  <button className="secondary" disabled={busy!==""} onClick={()=>decide(item,"RETURN")}>보완 요청</button>
                  <button className="primary" disabled={busy!==""} onClick={()=>decide(item,"APPROVE")}>승인</button>
                </div>
              )}
            </article>
          ))}
          {!inbox?.items.length && <p className="case-empty">제출된 공식 보고서가 없습니다.</p>}
        </div>
      </section>
    );
  }

  if(view==="DISTRIBUTION"){
    return (
      <section className="tower-card erp08-admin-panel">
        <div className="panel-title"><div><strong>다학교 심리검사 배포</strong><span>지역 범위 안의 학교를 선택해 동일한 Published 검사를 일괄 배정합니다.</span></div></div>
        {message && <p className="teacher-success">{message}</p>}
        <div className="erp08-distribution-config">
          <label>배포 검사
            <select value={versionId} onChange={(event)=>setVersionId(event.target.value)}>
              {(distribution?.assessments || []).map((item)=><option key={item.versionId} value={item.versionId}>{item.instrumentName} · {item.title} · {item.versionLabel}</option>)}
            </select>
          </label>
          <div className="erp08-distribution-summary">
            <span>{selectedAssessment ? `${selectedAssessment.gradeMin || "-"}~${selectedAssessment.gradeMax || "-"}학년` : "검사 없음"}</span>
            <strong>{selectedSchools.length}개 학교 선택</strong>
          </div>
        </div>
        <div className="erp08-school-picker">
          <label className="erp08-select-all"><input type="checkbox" checked={allSelected} onChange={()=>{
            setSelectedSchools(allSelected ? [] : (distribution?.schools || []).map((item)=>item.schoolId));
          }} />전체 학교 선택</label>
          {(distribution?.schools || []).map((school)=>(
            <label key={school.schoolId}><input type="checkbox" checked={selectedSchools.includes(school.schoolId)} onChange={()=>{
              setSelectedSchools((current)=>current.includes(school.schoolId) ? current.filter((id)=>id!==school.schoolId) : [...current,school.schoolId]);
            }} /><span><strong>{school.name}</strong><small>{school.code || "학교코드 없음"} · 학생 {school.studentCount}명</small></span></label>
          ))}
        </div>
        <button className="primary" disabled={busy!=="" || !versionId || !selectedSchools.length} onClick={distribute}>{busy==="distribution" ? "배포 중…" : "선택 학교에 검사 배포"}</button>
        <div className="erp08-distribution-history">
          <strong>최근 배포 작업</strong>
          {(distribution?.distributions || []).map((item)=><div key={item.distributionId}><span><b>{item.assessmentTitle}</b><small>{fmt(item.createdAt)} · {item.schoolCount}개 학교</small></span><span>{statusLabel(item.status)} · 성공 {item.succeededCount} / 실패 {item.failedCount}</span></div>)}
          {!distribution?.distributions.length && <p className="case-empty">배포 이력이 없습니다.</p>}
        </div>
      </section>
    );
  }

  return (
    <section className="tower-card erp08-admin-panel">
      <div className="panel-title"><div><strong>운영 준비상태</strong><span>교육청/기관 운영에 필요한 Gate를 실제 설정값으로 확인합니다.</span></div><span className={readiness?.ready ? "status-pill good" : "status-pill"}>{readiness?.ready ? "READY" : "ACTION REQUIRED"}</span></div>
      {message && <p className="teacher-success">{message}</p>}
      <div className="erp08-readiness-grid">
        {(readiness?.checks || []).map((check)=><div key={check.key} className={check.pass ? "pass" : "fail"}><span>{check.pass ? "PASS" : "CHECK"}</span><strong>{check.label}</strong></div>)}
      </div>
      <div className="erp08-queue-card">
        <div><span>대기</span><strong>{readiness?.externalNotificationQueue.queued ?? 0}</strong></div>
        <div><span>전송 수락</span><strong>{readiness?.externalNotificationQueue.accepted ?? 0}</strong></div>
        <div><span>재시도</span><strong>{readiness?.externalNotificationQueue.retryableFailure ?? 0}</strong></div>
        <div><span>영구 실패</span><strong>{readiness?.externalNotificationQueue.permanentFailure ?? 0}</strong></div>
      </div>
      <button className="secondary" disabled={busy!==""} onClick={retryNotifications}>{busy==="retry" ? "호출 중…" : "외부 알림 재시도"}</button>
      {!readiness?.ready && <p className="muted">CHECK 항목을 해결하기 전에는 Production Ready로 표시하지 않습니다.</p>}
    </section>
  );
}
